import { Bike, CheckCircle, Clock, Target, Users } from "lucide-react";
import type React from "react";
import Seo from "../components/Seo";

const CycleCoBasics: React.FC = () => {
  const features = [
    {
      icon: <Bike className="w-8 h-8" />,
      title: "Bike Setup & Safety",
      description:
        "Get your bike adjusted properly for a comfortable, safe ride.",
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Proper Form & Technique",
      description:
        "Learn the fundamentals to ride efficiently and reduce the risk of injury.",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Studio Orientation",
      description:
        "Get familiar with our bikes, shoes, studio etiquette, and what to expect during class.",
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Pacing & Rhythm",
      description:
        "Understand resistance, cadence, and how to ride confidently to the beat.",
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Build Confidence",
      description:
        "Ask questions, ease any nerves, and feel fully prepared for your first class.",
    },
  ];

  return (
    <div
      className="min-h-full"
      style={{ backgroundColor: "var(--color-complementary)" }}
    >
      <Seo
        title="Cycle Co Basics"
        description="New to indoor cycling? Cycle Co Basics covers bike setup, proper form, studio orientation, pacing, and rhythm so you feel confident for your first class."
        path="/experience/cyclecobasics"
        image="/images/basics.jpg"
      />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: "var(--color-text)" }}
            >
              Cycle Co Basics
            </h1>
            <p
              className="text-xl md:text-2xl font-medium mb-4"
              style={{ color: "var(--color-text)" }}
            >
              New to indoor cycling? We've got you.
            </p>
            <p className="text-lg text-black/70 max-w-3xl mx-auto">
              Instead of attending a separate beginner class, simply arrive 30
              minutes before your first ride for a complimentary one-on-one
              introduction with one of our instructors.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Content */}
            <div>
              <h2
                className="text-2xl font-bold mb-6"
                style={{ color: "var(--color-text)" }}
              >
                During Your Personalized Session, You'll Learn
              </h2>
              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-black/10 rounded-full flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <div>
                      <h3
                        className="font-semibold mb-1"
                        style={{ color: "var(--color-text)" }}
                      >
                        {feature.title}
                      </h3>
                      <p className="text-black/70 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <img
                src="/images/basics.jpg"
                alt="Cycle Co instructors welcoming you to the studio"
                className="w-full h-[450px] object-cover rounded-lg shadow-xl"
                style={{ objectPosition: "center 41%" }}
              />
            </div>

            {/* Image */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img
                  src="/images/app.png"
                  alt="Cycle Co Basics class in session"
                  className="rounded-lg shadow-xl max-w-full h-auto w-full max-w-md lg:max-w-lg"
                />
                <div className="absolute -top-4 -left-4 bg-white rounded-lg shadow-lg p-4">
                  <div className="text-center">
                    <div
                      className="text-2xl font-bold"
                      style={{ color: "var(--color-text)" }}
                    >
                      30
                    </div>
                    <div className="text-xs text-black/60">MIN EARLY</div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-white rounded-lg shadow-lg p-4">
                  <div className="text-center">
                    <div
                      className="text-lg font-bold"
                      style={{ color: "var(--color-text)" }}
                    >
                      ALL LEVELS
                    </div>
                    <div className="text-xs text-black/60">WELCOME</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Closing Statement */}
          <p className="text-lg text-center text-black/70 max-w-3xl mx-auto mb-16">
            Our goal is simple: We want your first Cycle Co experience to feel
            welcoming, fun, and empowering—so you can focus on enjoying the
            ride.
          </p>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-white/30 backdrop-blur-sm rounded-lg p-8 max-w-2xl mx-auto">
              <h3
                className="text-xl font-semibold mb-3"
                style={{ color: "var(--color-text)" }}
              >
                Ready to Get Started?
              </h3>
              <p className="text-black/70 mb-6">
                Just let us know it's your first class when you book, and we'll
                take care of the rest.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/services"
                  className="inline-block px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                  style={{
                    backgroundColor: "var(--color-altBrand)",
                    color: "var(--color-text)",
                  }}
                >
                  Book Your Class
                </a>
                <a
                  href="/contact"
                  className="inline-block px-6 py-3 border-2 rounded-lg font-semibold transition-all hover:scale-105"
                  style={{
                    borderColor: "var(--color-altBrand)",
                    color: "var(--color-text)",
                  }}
                >
                  Ask Questions
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CycleCoBasics;
